import { useCallback, createContext, useState, type PropsWithChildren } from 'react';
import { FormattedMessage } from 'react-intl';

import {
  NotificationService,
  NOTIFICATION_TYPE_ERROR,
  NOTIFICATION_TYPE_SUCCESS,
  NOTIFICATION_DURATIONS,
} from 'ui-components/services/notification-service';
import { noop } from 'ui-components/default-props/noop';

import { sendLabelToBuyer } from '../../../../../store/returns/returns.api';
import { getCommaSeparatedEmailsFromRequestDetails } from '../../../../../store/returns/returns.helpers';
import { RETURN_ACTION_SEND_LABEL_CLICKED } from '../../../../../constants/analytics-events';
import { Segment } from '../../../../../services/analytics-services/segment';
import { useModal } from '../../../../../hooks/use-modal';
import { useSalesOrderIdContext } from '../../../../context/sales-order-id-context-provider';
import { useEntitiesSalesOrder } from '../../../../../hooks/entities/selectors/sales-orders';
import { MultiEmailInputModal } from '../../../../../components/modals/multi-email-input-modal';

import { messages } from './messages';
import type { ReturnDetails } from '../../enhanced-return-types';

type DropdownActionsContextType = {
  openModal: (data: ReturnDetails) => void;
};

export const DropdownActionsContext = createContext<DropdownActionsContextType>({
  openModal: noop,
});

export const DropdownActionsProvider = ({ children }: PropsWithChildren) => {
  const salesOrderId = useSalesOrderIdContext();
  const salesOrder = useEntitiesSalesOrder(salesOrderId) ?? {};
  const storeGuid = salesOrder?.store?.storeGuid;

  const [modalData, setModalData] = useState<ReturnDetails | null>(null);
  const [defaultEmail, setDefaultEmail] = useState('');
  const { isOpen: isSendLabelModalOpen, open: openSendLabelModal, close: closeSendLabelModal } = useModal();

  const openModal = useCallback(
    (data: ReturnDetails) => {
      setModalData(data);
      setDefaultEmail(getCommaSeparatedEmailsFromRequestDetails(data));
      openSendLabelModal();
    },
    [openSendLabelModal],
  );

  const handleSendLabel = useCallback(
    async (emails: string[]) => {
      if (!modalData) return;

      try {
        Segment.trackEvent(RETURN_ACTION_SEND_LABEL_CLICKED, {
          type: RETURN_ACTION_SEND_LABEL_CLICKED,
          enhanced: true,
          requestId: modalData.id,
          storeGuid,
          emails,
        });

        await sendLabelToBuyer(modalData.id, storeGuid, emails);
        NotificationService.showNotification({
          notificationType: NOTIFICATION_TYPE_SUCCESS,
          title: <FormattedMessage {...messages.sendLabelSuccessTitle} />,
          content: <FormattedMessage {...messages.sendLabelSuccessContent} values={{ count: emails.length }} />,
          duration: NOTIFICATION_DURATIONS.SHORT,
        });
        closeSendLabelModal();
      } catch {
        NotificationService.showNotification({
          notificationType: NOTIFICATION_TYPE_ERROR,
          title: <FormattedMessage {...messages.errorTitle} />,
          content: <FormattedMessage {...messages.errorContent} />,
          duration: NOTIFICATION_DURATIONS.SHORT,
        });
      }
    },
    [storeGuid, modalData, closeSendLabelModal],
  );

  return (
    <DropdownActionsContext.Provider value={{ openModal }}>
      {children}

      {isSendLabelModalOpen && modalData && (
        <MultiEmailInputModal
          defaultEmail={defaultEmail}
          modalBodyText={messages.sendReturnLabelModalDescription}
          modalHeaderText={messages.sendReturnLabelText}
          onClose={closeSendLabelModal}
          onSend={handleSendLabel}
        />
      )}
    </DropdownActionsContext.Provider>
  );
};
